
import React from 'react';
import { Treatment, SiteSettings } from '../types';

interface PricingProps {
  treatments: Treatment[];
  settings: SiteSettings;
  categories: string[];
}

const Pricing: React.FC<PricingProps> = ({ treatments, settings, categories }) => {
  return (
    <div className="w-full bg-[#0a0a0a] min-h-screen pt-32 pb-24">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-24 animate-fadeIn">
          <span className="text-primary text-[10px] font-black uppercase tracking-[0.4em] mb-4 block">Investeer in uzelf</span>
          <h1 className="font-display text-5xl md:text-8xl text-white mb-8">Tarieven</h1>
          <p className="text-white/40 text-lg max-w-2xl mx-auto font-light leading-relaxed">
            Bij MD An De Cuyper hanteren we transparante tarieven. 
            Elke behandeling is maatwerk; een definitieve prijsopgave volgt na uw persoonlijke consultatie.
          </p>
        </div>

        {/* Categories & Treatments */}
        <div className="space-y-32">
          {categories.map((category) => {
            const categoryTreatments = treatments.filter(t => t.category === category);
            if (categoryTreatments.length === 0) return null;

            return (
              <div key={category} className="space-y-10 animate-fadeIn">
                <div className="flex items-center gap-8">
                  <h2 className="font-display text-3xl text-primary uppercase tracking-[0.2em]">{category}</h2>
                  <div className="h-[1px] flex-grow bg-white/5"></div>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  {categoryTreatments.map((treatment) => (
                    <div 
                      key={treatment.id} 
                      className="group flex flex-col md:flex-row justify-between items-start md:items-center py-8 px-8 md:px-12 bg-white/[0.02] border border-white/5 rounded-[2rem] hover:bg-white/[0.05] hover:border-primary/20 transition-all duration-500"
                    >
                      <div className="flex-grow mb-6 md:mb-0">
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">{treatment.name}</h3>
                        <p className="text-white/40 text-sm font-light max-w-xl leading-relaxed">{treatment.description}</p>
                      </div>
                      <div className="flex flex-col items-start md:items-end min-w-[140px]">
                        <span className="text-2xl font-display text-white mb-1">
                          {treatment.price.toLowerCase().includes('bespreken') || treatment.price.includes('-') ? (
                            treatment.price
                          ) : (
                            treatment.price === '0' ? 'Gratis' : `€ ${treatment.price}`
                          )}
                        </span>
                        <div className="flex items-center gap-3">
                           <span className="text-[10px] text-white/30 uppercase tracking-widest font-bold">
                            {treatment.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="mt-32 p-12 bg-white/[0.03] border border-white/10 rounded-[3rem] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32 blur-[100px]"></div>
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="font-display text-3xl text-white mb-6">Uw Consultatie</h3>
              <p className="text-white/50 text-base leading-relaxed mb-8">
                Voor elke nieuwe patiënt of nieuwe behandeling is een medische consultatie (€50) verplicht. 
                Tijdens dit gesprek stellen we een behandelplan op dat volledig is afgestemd op uw anatomie en wensen.
              </p>
              <div className="flex flex-wrap gap-6 text-[10px] font-black uppercase tracking-widest text-primary">
                <span className="flex items-center gap-2 decoration-primary/30 underline underline-offset-4">✓ Medische Expertise</span>
                <span className="flex items-center gap-2 decoration-primary/30 underline underline-offset-4">✓ Natuurlijk Resultaat</span>
              </div>
            </div>
            <div className="text-right">
              <a 
                href={settings.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary text-black px-12 py-6 text-xs font-black uppercase tracking-[0.3em] hover:bg-white transition-all shadow-2xl shadow-primary/20"
              >
                Maak een afspraak
              </a>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="mt-16 text-center text-white/20 text-[10px] uppercase tracking-[0.3em] max-w-xl mx-auto">
          Alle prijzen zijn indicatief en inclusief BTW. De definitieve prijs wordt bepaald tijdens de consultatie.
        </p>
      </div>
    </div>
  );
};

export default Pricing;
